import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDefaultTheme, getThemeConfig, isValidTheme } from '../config/themes';
import { onThemeChange } from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children, initialTheme = null }) => {
  // Fonction pour détecter si on est en mode production (domaines spécifiques)
  const isProductionDomain = () => {
    const { hostname } = window.location;
    const productionDomains = [
      'tropicaliente.ch',
      'ragescream.com',
      'spkombat.com',
      'standup-comedy.ch',
      'livecomedy.ch',
      'fabiomeyer.com',
      'jemmyx.com',
      'wildpeaks.tv'
    ];
    
    return productionDomains.some(domain => 
      hostname === domain || hostname.includes(domain)
    );
  };

  // Déterminer le thème initial depuis l'URL, localStorage, ou défaut
  const getInitialTheme = () => {
    if (initialTheme && isValidTheme(initialTheme)) {
      return initialTheme;
    }

    // Vérifier l'URL pour un paramètre de thème
    const urlParams = new URLSearchParams(window.location.search);
    const urlTheme = urlParams.get('theme');
    if (urlTheme && isValidTheme(urlTheme)) {
      return urlTheme;
    }

    // PRIORITÉ 1: Vérifier localStorage (sélection manuelle via sidebar)
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme && isValidTheme(savedTheme)) {
      return savedTheme;
    }

    // PRIORITÉ 2: Vérifier le port/host pour déterminer le thème (détection automatique)
    const { hostname, port } = window.location;
    const hostPort = `${hostname}:${port}`;

    console.log('hostname', hostname);
    
    // Mapping des ports vers les thèmes
    if (hostPort === 'localhost:3001') {
      return 'rock';
    }
    if (hostPort === 'localhost:3002') {
      return 'latino';
    }
    if (hostPort === 'localhost:3003') {
      return 'standup';
    }
    if (hostPort === 'localhost:3004') {
      return 'culture_fr';
    }
    if (hostPort === 'localhost:3005') {
      return 'spkombat';
    }
    if (hostPort === 'localhost:3006') {
      return 'fabiomeyer'; // Définir fabiomeyer comme thème par défaut
    }
    if (hostPort === 'localhost:3007') {
      return 'jemmyx'; // Définir jemmyx comme thème par défaut
    }
    if (hostPort === 'localhost:3008') {
      return 'sport'; // Définir sport comme thème par défaut
    }
    if (hostPort === 'localhost:3009') {
      return 'wildpeaks'; // Définir wildpeaks comme thème par défaut
    }

    // Vérifier le sous-domaine et domaine pour déterminer le thème (fallback pour production)
    if (hostname.includes('latino') || hostname.startsWith('latino.') || hostname.includes('tropicaliente')) {
      return 'latino';
    }
    if (hostname.includes('standup') || hostname.startsWith('standup.') || hostname.includes('standup-comedy')) {
      return 'standup';
    }

    if (hostname.includes('spkombat')) {
      return 'spkombat';
    }

    if (hostname.includes('fabiomeyer.com')) {
      return 'fabiomeyer'; // Définir fabiomeyer comme thème par défaut
    }

    if (hostname.includes('jemmyx.com')) {
      return 'jemmyx'; // Définir jemmyx comme thème par défaut
    }

    if (hostname.includes('sport.com') || hostname.includes('wildpeaks')) {
      return 'sport'; // Définir sport comme thème par défaut
    }

    // Défaut vers rock/metal
    return 'rock';
  };

  const [currentTheme, setCurrentTheme] = useState(getInitialTheme());
  const [themeConfig, setThemeConfig] = useState(getThemeConfig(currentTheme));
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Fonction pour changer de thème
  const changeTheme = (newTheme) => {
    if (isValidTheme(newTheme)) {
      setCurrentTheme(newTheme);
      setThemeConfig(getThemeConfig(newTheme));
      setSelectedLabel(null); // Reset label selection when changing theme
      localStorage.setItem('selectedTheme', newTheme);
      
      // Notifier le service API du changement de thème
      onThemeChange(newTheme);
      
      // Mettre à jour le titre de la page
      document.title = getThemeConfig(newTheme).seo.title;
      
      // Mettre à jour la meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.content = getThemeConfig(newTheme).seo.description;
      }
    }
  };

  // Fonction pour sélectionner/désélectionner un label
  const selectLabel = (label) => {
    if (selectedLabel?.id === label?.id) {
      setSelectedLabel(null); // Désélectionner si déjà sélectionné
    } else {
      setSelectedLabel(label); // Sélectionner le nouveau label
    }
  };

  // Fonction pour filtrer les labels
  const getFilteredLabels = () => {
    if (!selectedLabel) {
      return themeConfig.labels; // Retourner tous les labels si aucun n'est sélectionné
    }
    return themeConfig.labels.filter(label => label.id === selectedLabel.id);
  };

  // Appliquer les styles CSS personnalisés pour le thème
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', themeConfig.primary_color);
    root.style.setProperty('--theme-secondary', themeConfig.secondary_color);
    root.style.setProperty('--theme-accent', themeConfig.accent_color);
    
    // Mettre à jour le titre et la description
    document.title = themeConfig.seo.title;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = themeConfig.seo.description;
    }

    // Mettre à jour les meta tags Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = themeConfig.seo.title;
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.content = themeConfig.seo.description;
    
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.content = themeConfig.seo.title;
    
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.content = themeConfig.seo.description;
    
  }, [themeConfig]);

  const toggleSidebar = () => {
  setIsSidebarOpen((prev) => {
    const newState = !prev;
    return newState;
  });
  };

  const value = {
    currentTheme,
    themeConfig,
    changeTheme,
    isTheme: (theme) => currentTheme === theme,
    labels: themeConfig.labels,
    selectedLabel,
    selectLabel,
    filteredLabels: getFilteredLabels(),
    isProductionDomain: isProductionDomain(),
    isSidebarOpen,
    toggleSidebar
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
